import React, { useRef, useCallback, useEffect, useState } from "react";
import { ISortNorth } from "../../icons/ISortNorth";
import { ISortSouth } from "../../icons/ISortSouth";
import { ISortSwapVert } from "../../icons/ISortSwapVert";
import { SkillMapData, DisplayMode, SortState } from "./types";
import { useTextMeasurement } from "./hooks/useTextMeasurement";
import { useColumnWidths } from "./hooks/useColumnWidths";
import { useSorting } from "./hooks/useSorting";
import { useCustomScrollbars } from "./hooks/useCustomScrollbars";
import { WIDTH_CONSTRAINTS } from "./constants";
import "./style.scss";

/**
 * レベル値を表示用文字列に変換（画面表示・CSV出力共通）
 * @param levelValue レベル値
 * @returns 表示用文字列（0や空の場合は"-"）
 */
const formatLevelValue = (levelValue: number): string => {
  return (!levelValue || levelValue === 0) ? "-" : levelValue.toString();
};

interface SkillMapTableProps {
  data: SkillMapData;
  mode: DisplayMode;
  sortState?: SortState;
  onSortChange?: (sortState: SortState) => void;
}

const SkillMapTable: React.FC<SkillMapTableProps> = ({ data, mode, sortState: propsSortState, onSortChange }) => {
  const { process_name, user_name, header, structure, data: rowData } = data;

  // モードに応じてヘッダー名を決定
  const headerName = mode === "process" ? process_name : user_name;

  // テキスト幅測定用のフック
  const { measureMaxTextWidth, measureTextWidth } = useTextMeasurement();

  // スクロール同期用のref
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const dataScrollAreaRef = useRef<HTMLDivElement>(null);

  // 動的な固定列幅
  const [fixedColumnWidth, setFixedColumnWidth] = useState(200);

  // 各データ列の動的幅
  const [dataColumnWidths, setDataColumnWidths] = useState<number[]>([]);

  // カスタムスクロールバー用のフック
  const scrollbars = useCustomScrollbars({
    headerContainerRef,
    dataScrollAreaRef,
    fixedColumnWidth
  });

  // ソート処理用のフック
  const { sortState, handleDetailColumnClick, getSortedData } = useSorting({
    propsSortState,
    structure,
    rowData,
    onSortChange
  });


  // 細項目の配列を作成（列幅計算用）
  const getDetailColumns = useCallback(() => {
    const detailColumns: any[] = [];
    header.forEach((largeCategory) => {
      largeCategory.product_medium_categories.forEach((mediumCategory: any) => {
        mediumCategory.product_small_categories.forEach((smallCategory: any) => {
          smallCategory.product_detail_categories.forEach((detailCategory: any) => {
            detailColumns.push(detailCategory);
          });
        });
      });
    });
    return detailColumns;
  }, [header]);

  const detailColumns = getDetailColumns();

  // 列幅計算用のフック
  const {
    calculateFixedColumnWidth,
    calculateDataColumnWidths,
    calculateHeaderWidths
  } = useColumnWidths({
    headerName,
    rowData,
    mode,
    header,
    getDetailColumns,
    measureMaxTextWidth,
    measureTextWidth
  });

  // 固定列幅を初期化・更新
  useEffect(() => {
    const calculatedWidth = calculateFixedColumnWidth();
    setFixedColumnWidth(calculatedWidth);
  }, [calculateFixedColumnWidth]);

  // データ列幅を初期化・更新
  useEffect(() => {
    const calculatedWidths = calculateDataColumnWidths();
    setDataColumnWidths(calculatedWidths);
  }, [calculateDataColumnWidths]);

  // リサイズ時とデータ変更時にスクロールバーを更新
  useEffect(() => {
    const headerElement = headerContainerRef.current;
    const dataElement = dataScrollAreaRef.current;

    if (headerElement && dataElement) {
      // 初期スクロールバー状態を更新
      scrollbars.updateScrollbars();

      // リサイズ時にスクロールバーを更新
      const resizeObserver = new ResizeObserver(() => {
        scrollbars.updateScrollbars();
      });
      resizeObserver.observe(headerElement);
      resizeObserver.observe(dataElement);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [scrollbars, dataColumnWidths]);

  // ヘッダー部分のレンダリング
  const renderHeaderRows = () => {
    const headerCellWidths = calculateHeaderWidths(dataColumnWidths);
    const adjustedDataColumnWidths = headerCellWidths.adjustedDataColumnWidths;

    return (
      <div className="header-rows-wrapper">
        {/* 4行分の高さを持つprocess-name-cell */}
        <div
          className="process-name-cell-container"
          style={{ width: `${fixedColumnWidth}px` }}
        >
          <div className="process-name-cell">
            <div className="process-name-cell-content">
              {headerName}
            </div>
          </div>
        </div>

        {/* ヘッダー行の内容 */}
        <div className="header-rows-content">
          {/* 大項目行 */}
          <div className="header-row large-row">
            <div className="scrollable-header-content">
              {header.map((largeCategory, index) => (
                <div
                  key={`large-${largeCategory.product_large_category_id || 'null'}-${index}`}
                  className={`header-cell large-category ${!largeCategory.product_large_category_name ? 'category-name-empty' : ''}`}
                  style={{ width: `${headerCellWidths.large[index] || WIDTH_CONSTRAINTS.DATA_COLUMN.DEFAULT}px` }}
                >
                  <div className="large-category-content">
                    {largeCategory.product_large_category_name || "／"}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 中項目行 */}
          <div className="header-row medium-row">
            <div className="scrollable-header-content">
              {(() => {
                let mediumIndex = 0;
                return header.map((largeCategory) =>
                  largeCategory.product_medium_categories.map((mediumCategory: any) => {
                    const width = headerCellWidths.medium[mediumIndex] || mediumCategory.size * WIDTH_CONSTRAINTS.DATA_COLUMN.DEFAULT;
                    mediumIndex++;
                    return (
                      <div
                        key={`medium-${mediumCategory.product_medium_category_id || 'null'}-${mediumIndex}`}
                        className={`header-cell medium-category ${!mediumCategory.product_medium_category_name ? 'category-name-empty' : ''}`}
                        style={{ width: `${width}px` }}
                      >
                        <div className="medium-category-content">
                          {mediumCategory.product_medium_category_name || "／"}
                        </div>
                      </div>
                    );
                  })
                );
              })()}
            </div>
          </div>

          {/* 小項目行 */}
          <div className="header-row small-row">
            <div className="scrollable-header-content">
              {(() => {
                let smallIndex = 0;
                return header.map((largeCategory) =>
                  largeCategory.product_medium_categories.map((mediumCategory: any) =>
                    mediumCategory.product_small_categories.map((smallCategory: any) => {
                      const width = headerCellWidths.small[smallIndex] || smallCategory.size * WIDTH_CONSTRAINTS.DATA_COLUMN.DEFAULT;
                      smallIndex++;
                      return (
                        <div
                          key={`small-${smallCategory.product_small_category_id || 'null'}-${smallIndex}`}
                          className={`header-cell small-category ${!smallCategory.product_small_category_name ? 'category-name-empty' : ''}`}
                          style={{ width: `${width}px` }}
                        >
                          <div className="small-category-content">
                            {smallCategory.product_small_category_name || "／"}
                          </div>
                        </div>
                      );
                    })
                  )
                );
              })()}
            </div>
          </div>

          {/* 細項目行 */}
          <div className="header-row detail-row">
            <div className="scrollable-header-content">
              {detailColumns.map((detailCategory, detailIndex) => (
                <div
                  key={`detail-${detailCategory.product_detail_category_id || 'null'}-${detailIndex}`}
                  className={`header-cell detail-category ${!detailCategory.product_detail_category_name ? 'category-name-empty' : ''}`}
                  style={{
                    width: `${adjustedDataColumnWidths[detailIndex] || WIDTH_CONSTRAINTS.DATA_COLUMN.DEFAULT}px`,
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    handleDetailColumnClick(detailIndex);
                  }}
                >
                  <div className="detail-category-content">
                    <div className="detail-text">
                      {detailCategory.product_detail_category_name || "／"}
                    </div>
                    <div className="sort-icon">
                      {sortState.columnIndex === detailIndex && (
                        <>
                          {sortState.direction === 'desc' ? <ISortSouth /> : <ISortNorth />}
                        </>
                      )}
                      {sortState.columnIndex !== detailIndex && (
                        <ISortSwapVert />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // データ行のレンダリング
  const renderDataRows = () => {
    const headerCellWidths = calculateHeaderWidths(dataColumnWidths);
    const adjustedDataColumnWidths = headerCellWidths.adjustedDataColumnWidths;
    const sortedData = getSortedData(); // ソートされたデータを取得

    return sortedData.map((row) => {
      const rowId = mode === "process" ? row.user_id : row.machining_process_id;
      const rowName = mode === "process" ? row.user_name : row.process_name;

      // レベル値の配列を作成
      const levelValues: (string | number)[] = [];
      structure.forEach((structureItem) => {
        const matchingLevel = row.levels.find(level =>
          level.product_large_category_id === structureItem.product_large_category_id &&
          level.product_medium_category_id === structureItem.product_medium_category_id &&
          level.product_small_category_id === structureItem.product_small_category_id &&
          level.product_detail_category_id === structureItem.product_detail_category_id
        );
        const levelValue = matchingLevel ? matchingLevel.product_level_value : 0;
        levelValues.push(formatLevelValue(levelValue));
      });

      return (
        <div key={`data-row-${rowId}`} className="data-row">
          <div
            className="user-name-cell fixed-data-cell"
            style={{ width: `${fixedColumnWidth}px` }}
          >
            <div className="fixed-data-cell-content">
              {rowName}
            </div>
          </div>
          <div className="scrollable-data-content">
            {levelValues.map((levelValue, levelIndex) => (
              <div
                key={`level-${rowId}-${levelIndex}`}
                className="level-value-cell"
                style={{ width: `${adjustedDataColumnWidths[levelIndex] || WIDTH_CONSTRAINTS.DATA_COLUMN.DEFAULT}px` }}
              >
                {levelValue}
              </div>
            ))}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="skill-map-table-container">
      {/* ヘッダー部分（固定） */}
      <div className="header-section">
        {/* カスタムスクロールバー */}
        {scrollbars.horizontal.thumbWidth > 0 && (
          <div
            className="custom-scrollbar"
            style={{
              marginRight: scrollbars.vertical.thumbHeight > 0 ? undefined : '0px'
            }}
          >
            <div
              className="custom-scrollbar-spacer"
              style={{ width: `${fixedColumnWidth}px` }}
            ></div>
            <div
              className="custom-scrollbar-track"
              ref={scrollbars.horizontal.trackRef}
              onClick={scrollbars.horizontal.handleTrackClick}
            >
              <div
                className={`custom-scrollbar-thumb ${scrollbars.horizontal.isDragging ? 'dragging' : ''}`}
                ref={scrollbars.horizontal.thumbRef}
                style={{
                  width: `${scrollbars.horizontal.thumbWidth}px`,
                  left: `${scrollbars.horizontal.thumbPosition}px`,
                }}
                onMouseDown={scrollbars.horizontal.handleThumbMouseDown}
              />
            </div>
          </div>
        )}

        <div className="header-area-with-scrollbar">
          <div
            className="header-scroll-container"
            ref={headerContainerRef}
            onScroll={scrollbars.horizontal.handleScroll}
          >
            <div className="header-content-wrapper">
              {renderHeaderRows()}
            </div>
          </div>

          {/* ヘッダー右側のスペーサー（縦スクロールバーと同じ幅） */}
          {scrollbars.vertical.thumbHeight > 0 && (
            <div className="header-vertical-spacer"></div>
          )}
        </div>
      </div>

      {/* データ部分（縦スクロール可能） */}
      <div className="data-section">
        <div className="data-area-with-scrollbar">
          <div className="data-wrapper">
            <div
              className="data-scroll-area"
              ref={dataScrollAreaRef}
              onScroll={scrollbars.vertical.handleScroll}
            >
              <div className="data-content-wrapper">
                {renderDataRows()}
              </div>
            </div>
          </div>

          {/* 縦スクロールバー */}
          {scrollbars.vertical.thumbHeight > 0 && (
            <div className="vertical-custom-scrollbar">
              <div
                className="vertical-custom-scrollbar-track"
                ref={scrollbars.vertical.trackRef}
                onClick={scrollbars.vertical.handleTrackClick}
              >
                <div
                  className={`vertical-custom-scrollbar-thumb ${scrollbars.vertical.isDragging ? 'dragging' : ''}`}
                  ref={scrollbars.vertical.thumbRef}
                  style={{
                    height: `${scrollbars.vertical.thumbHeight}px`,
                    top: `${scrollbars.vertical.thumbPosition}px`,
                  }}
                  onMouseDown={scrollbars.vertical.handleThumbMouseDown}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SkillMapTable;